/**
 * Ported from the lekterion codebase (`src/components/autoplay-provider.tsx`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 */
import * as React from "react";

/**
 * Global "autoplay audio" preference (§10). When on, the SelectionLayer replays an
 * already-cached TTS clip automatically — a short phrase as soon as its menu
 * opens, a single word once its engagement gate is cleared. Read-only: it only
 * ever replays clips the Read-aloud button synthesized, never generating audio.
 *
 * Persisted to localStorage (mirrors ThemeProvider) and synced across tabs.
 * Default ON — the toggle exists to silence it.
 */

interface AutoplayState {
  autoplay: boolean;
  setAutoplay: (next: boolean) => void;
}

const AutoplayContext = React.createContext<AutoplayState | undefined>(
  undefined,
);

const STORAGE_KEY = "lekterion:autoplayAudio";

function readStored(defaultOn: boolean): boolean {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
  } catch {
    /* storage unavailable — fall back to default */
  }
  return defaultOn;
}

export function AutoplayProvider({
  children,
  defaultOn = true,
}: {
  children: React.ReactNode;
  defaultOn?: boolean;
}): React.JSX.Element {
  const [autoplay, setAutoplayState] = React.useState<boolean>(() =>
    readStored(defaultOn),
  );

  const setAutoplay = React.useCallback((next: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* storage unavailable — preference stays in-session */
    }
    setAutoplayState(next);
  }, []);

  // Mirror a toggle made in another tab.
  React.useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.storageArea !== localStorage || e.key !== STORAGE_KEY) return;
      setAutoplayState(e.newValue === "1");
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const value = React.useMemo(
    () => ({ autoplay, setAutoplay }),
    [autoplay, setAutoplay],
  );

  return (
    <AutoplayContext.Provider value={value}>
      {children}
    </AutoplayContext.Provider>
  );
}

export function useAutoplay(): AutoplayState {
  const ctx = React.useContext(AutoplayContext);
  if (ctx === undefined) {
    throw new Error("useAutoplay must be used within an AutoplayProvider");
  }
  return ctx;
}
